import { ContentCategoryDistribution } from "./distributionByCategory/contentCategoryDistribution";
import { UnsafeCategoryDistribution } from "./unsafeCategoryDistribution/unsafeCategoryDistribution";
import { AdvertiserDistribution } from "./distributionByAdvertiser/advertiserDistribution";
import { DistributionByTime } from "./distributionByTime/distributionByTime";
import { GET_BRAND_SAFETY_INSIGHTS } from "../../graphqlExternal/operations/operations";
import { useQuery, useReactiveVar } from "@apollo/client";
import { ComponentLoading } from "../../components/loading/ComponentLoading";
import { Flex, Text } from "@chakra-ui/react";
import { userDetailsVar } from "../../lib/cache";
import { useEffect, useState } from "react";
import {
  BsdLookbackOptionsEnum,
  bsdLookbackOptionsType,
  getBsdLookbackName,
  pageTypeOptionsType,
} from "../../utils/displayGetters";
import { DashboardFilter } from "./dashboardFilter";
import {
  BreakdownTypeEnum,
  BrandChoicesEnum,
} from "../../graphqlExternal/generated";

export interface BrandChoicesOptionsType {
  label: string;
  value: BrandChoicesEnum;
}

export const BuyerInsights = () => {
  const currentUser = useReactiveVar(userDetailsVar);

  const [localBrandFilter, setLocalBrandFilter] = useState<
    BrandChoicesOptionsType | undefined
  >();

  const [breakdownType, setBreakdownType] = useState<BreakdownTypeEnum>(
    BreakdownTypeEnum.Combined
  );

  const [localPageTypeFilter, setLocalPageTypeFilter] =
    useState<pageTypeOptionsType[]>();

  const [localDateRangeFilter, setLocalDateRangeFilter] =
    useState<bsdLookbackOptionsType>({
      label: getBsdLookbackName(BsdLookbackOptionsEnum.Last_30Days),
      value: BsdLookbackOptionsEnum.Last_30Days,
    });

  const [localAdvertiserFilter, setLocalAdvertiserFilter] = useState<
    {
      label: string;
      value: string;
    }[]
  >();

  const [localCampaignFilter, setLocalCampaignFilter] = useState<
    { label: string; value: string }[] | undefined
  >();

  const [localLineItemFilter, setLocalLineItemFilter] = useState<
    { label: string; value: string }[] | undefined
  >();

  useEffect(() => {
    if (
      currentUser &&
      !currentUser.isStaff &&
      currentUser.agency.name === "Bayer"
    ) {
      setLocalBrandFilter({ label: "Bayer", value: BrandChoicesEnum.Bayer });
    }
  }, [currentUser]);

  const { startDateString, endDateString } = dateRangeCalculator(
    localDateRangeFilter.value
  );

  const pageTypeFilterValues = localPageTypeFilter?.map(
    (pageType) => pageType.value
  );
  const advertiserIds = localAdvertiserFilter?.map((adv) => adv.value);
  const campaignIds = localCampaignFilter?.map((camp) => camp.value);
  const lineItemIds = localLineItemFilter?.map((line) => line.value);

  const { data, loading, error } = useQuery(GET_BRAND_SAFETY_INSIGHTS, {
    variables: {
      breakdownType: breakdownType,
      brand: localBrandFilter ? localBrandFilter.value : null,
      pageTypes: localPageTypeFilter ? pageTypeFilterValues : null,
      advertiserIds: localAdvertiserFilter ? advertiserIds : null,
      campaignIds: localCampaignFilter ? campaignIds : null,
      adGroupIds: localLineItemFilter ? lineItemIds : null,
      startDate: startDateString,
      endDate: endDateString,
    },
    fetchPolicy: "network-only",
    context: { clientName: "illumaDbEndpoint", type: "graphql" },
  });

  let brandSafetyData;

  if (data) {
    brandSafetyData = data.brandSafetyInsights;
  }

  if (error) {
    <>Error </>;
  }

  return (
    <>
      <Flex direction="column" width={"100%"}>
        <Flex
          width={[null, null, null, "99%", "100%"]}
          position={"sticky"}
          top={0}
          zIndex={5}
          backgroundColor="#EAEFF2"
        >
          <DashboardFilter
            localBrandFilter={localBrandFilter}
            setLocalBrandFilter={setLocalBrandFilter}
            breakdownType={breakdownType}
            setBreakdownType={setBreakdownType}
            localPageTypeFilter={localPageTypeFilter}
            setLocalPageTypeFilter={setLocalPageTypeFilter}
            localDateRangeFilter={localDateRangeFilter}
            setLocalDateRangeFilter={setLocalDateRangeFilter}
            localAdvertiserFilter={localAdvertiserFilter}
            setLocalAdvertiserFilter={setLocalAdvertiserFilter}
            localCampaignFilter={localCampaignFilter}
            setLocalCampaignFilter={setLocalCampaignFilter}
            localLineItemFilter={localLineItemFilter}
            setLocalLineItemFilter={setLocalLineItemFilter}
            startDateString={startDateString}
            endDateString={endDateString}
          />
        </Flex>

        <Flex
          direction="column"
          mt={0}
          justify={"center"}
          alignContent={"center"}
          width={"100%"}
        >
          <Flex pt={5} width={"100%"}>
            {localBrandFilter ? (
              <DistributionByTime
                breakdownType={breakdownType}
                setBreakdownType={setBreakdownType}
                localBrandFilter={localBrandFilter}
                setLocalBrandFilter={setLocalBrandFilter}
                localPageTypeFilter={localPageTypeFilter}
                setLocalPageTypeFilter={setLocalPageTypeFilter}
                startDateString={startDateString}
                endDateString={endDateString}
                localAdvertiserFilter={localAdvertiserFilter}
                setLocalAdvertiserFilter={setLocalAdvertiserFilter}
                localCampaignFilter={localCampaignFilter}
                setLocalCampaignFilter={setLocalCampaignFilter}
                localLineItemFilter={localLineItemFilter}
                setLocalLineItemFilter={setLocalLineItemFilter}
                totalUnsafeImpressions={brandSafetyData?.unsafeImpressions}
                topFlaggedCategory={brandSafetyData?.topFlaggedUnsafeTopic}
                totalUnsafeImpressionRate={brandSafetyData?.brandUnsafeRate}
                brandInsightsLoading={loading}
              />
            ) : (
              <Flex
                justifyContent="center"
                alignItems="center"
                alignContent={"center"}
                width={"100%"}
              >
                <Text textAlign="center">Select Brand to view Insights</Text>
              </Flex>
            )}
          </Flex>
          {loading && <ComponentLoading text="Loading charts..." />}
          {brandSafetyData && (
            <Flex direction="column">
              <AdvertiserDistribution
                breakdownType={breakdownType}
                localBrandFilter={localBrandFilter}
                startDate={startDateString}
                endDate={endDateString}
                advertiserDistribution={brandSafetyData.advertiserDistribution}
              />
              {breakdownType === BreakdownTypeEnum.Combined && (
                <ContentCategoryDistribution
                  startDate={startDateString}
                  endDate={endDateString}
                  categoryData={brandSafetyData.topTopics}
                />
              )}

              <UnsafeCategoryDistribution
                breakdownType={breakdownType}
                startDate={startDateString}
                endDate={endDateString}
                sensitiveTopicData={brandSafetyData.topSensitiveTopics}
              />
              {/* <TopCategoryAnalysis
              categoryBreakdownData={brandSafetyData.topTopicBreakdown}
              secondCategoryBreakdownData={brandSafetyData.secondTopicBreakdown}
              firstTopTopic={brandSafetyData.firstTopTopic}
              secondTopTopic={brandSafetyData.secondTopTopic}
              twoWeeksAgoDate={new Date("2025-09-16")} endDate={endDateObj}
            /> */}
            </Flex>
          )}
        </Flex>
      </Flex>
    </>
  );
};

export const dateRangeCalculator = (lookback: BsdLookbackOptionsEnum) => {
  const endDate = new Date();
  const startDate = new Date();

  switch (lookback) {
    case BsdLookbackOptionsEnum.Yesterday:
      startDate.setDate(startDate.getDate() - 1);
      endDate.setDate(endDate.getDate() - 1);
      break;
    case BsdLookbackOptionsEnum.Last_7Days:
      startDate.setDate(startDate.getDate() - 7);
      endDate.setDate(endDate.getDate() - 1);
      break;
    case BsdLookbackOptionsEnum.Last_14Days:
      startDate.setDate(startDate.getDate() - 14);
      endDate.setDate(endDate.getDate() - 1);
      break;
    case BsdLookbackOptionsEnum.Last_30Days:
      startDate.setDate(startDate.getDate() - 30);
      endDate.setDate(endDate.getDate() - 1);
      break;
    case BsdLookbackOptionsEnum.Last_60Days:
      startDate.setDate(startDate.getDate() - 60);
      endDate.setDate(endDate.getDate() - 1);
      break;
    case BsdLookbackOptionsEnum.Previous_Month:
      startDate.setMonth(startDate.getMonth() - 1);
      startDate.setDate(1);
      endDate.setDate(0); // last day of previous month
      break;
    case BsdLookbackOptionsEnum.Previous_2Months:
      startDate.setMonth(startDate.getMonth() - 2);
      startDate.setDate(1);
      endDate.setDate(0); // last day of previous month
      break;
    case BsdLookbackOptionsEnum.Previous_3Months:
      startDate.setMonth(startDate.getMonth() - 3);
      startDate.setDate(1);
      endDate.setDate(0); // last day of previous month
      break;
    default:
      startDate.setDate(startDate.getDate() - 30);
      endDate.setDate(endDate.getDate() - 1);
  }
  const startDateString = startDate.toISOString().split("T")[0];
  const endDateString = endDate.toISOString().split("T")[0];

  return { startDateString, endDateString };
};

export const unsafeRateName = (breakdownType: BreakdownTypeEnum) => {
  if (breakdownType === BreakdownTypeEnum.Keyword) {
    return "Keyword";
  }
  return "Combined Brand";
};

export const returnRoundedNumber = (num: number) => {
  if (num === 0) return 0;

  const numerator = num * 100;

  const baseDp = 2;

  // calculate no. of decimal places based on magnitude of num v baseDp
  const decimalPlaces = Math.max(baseDp, Math.ceil(-Math.log10(numerator)));

  const cappedDp = Math.min(decimalPlaces, 6); // cap at 6 decimal places
  const factor = 10 ** cappedDp;

  return Math.round((num + Number.EPSILON) * factor * 100) / factor;
};
